import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { getRoleDisplay } from '../../utils/roleUtils';

const UserProfileDropdown: React.FC = () => {
  const { user, username, logout } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Generate initials for avatar
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const displayName = user?.full_name || username || 'User';
  const initials = getInitials(displayName);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Avatar + Menu Button (Twitter-style) */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-dark-bg-hover transition-colors focus:outline-none"
        aria-label="Account menu"
      >
        {/* Avatar Circle */}
        <div className="h-10 w-10 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-medium flex-shrink-0">
          {initials}
        </div>
        
        {/* Three Dots Menu Icon */}
        <svg 
          className="h-5 w-5 text-gray-600 dark:text-dark-text-secondary" 
          fill="none" 
          viewBox="0 0 24 24" 
          stroke="currentColor"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" 
          />
        </svg>
      </button>

      {/* Dropdown Menu - Twitter Style */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-72 bg-white dark:bg-dark-bg-secondary rounded-xl shadow-2xl border border-gray-200 dark:border-dark-border overflow-hidden z-50">
          {/* User Info Header */}
          <div className="px-4 py-3">
            <div className="flex items-start space-x-3">
              <div className="h-12 w-12 bg-blue-600 rounded-full flex items-center justify-center text-white text-base font-medium flex-shrink-0">
                {initials}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-base font-bold text-gray-900 dark:text-dark-text-primary truncate">
                  {displayName}
                </div>
                <div className="text-sm text-gray-500 dark:text-dark-text-muted truncate">
                  @{username}
                </div>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-200 dark:border-dark-border"></div>

          {/* Menu Items */}
          <div className="py-2">
            {(user?.role === 'VET_STAFF' || user?.role === 'PRACTICE_ADMIN' || user?.role === 'SYSTEM_ADMIN') && (
              <Link
                to="/dashboard"
                onClick={() => setIsOpen(false)}
                className="flex items-center w-full px-4 py-3 text-base text-gray-900 dark:text-dark-text-primary hover:bg-gray-50 dark:hover:bg-dark-bg-hover transition-colors"
              >
                <svg className="h-5 w-5 mr-3 text-gray-700 dark:text-dark-text-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                Dashboard
              </Link>
            )}

            {user?.role && (
              <div className="px-4 py-2 text-sm text-gray-500 dark:text-dark-text-muted">
                Role: {getRoleDisplay(user.role)}
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="border-t border-gray-200 dark:border-dark-border"></div>

          {/* Logout */}
          <div className="py-2">
            <button
              onClick={() => {
                logout();
                setIsOpen(false);
              }}
              className="flex items-center w-full px-4 py-3 text-base text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors font-medium"
            >
              <svg className="h-5 w-5 mr-3 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Logout
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfileDropdown;
